/**
 * Shared bracket highlight plugin utilities for BlockNote editors
 */

import type { BlockNoteEditor } from '@blocknote/core';

import {
  bracketHighlightPluginKey,
  createBracketHighlightPlugin,
  type OnBracketClick,
} from '../../components/BlockNoteBracketHighlightPlugin';

export {
  bracketHighlightPluginKey,
  type BracketMatch,
  createBracketHighlightPlugin,
  type OnBracketClick,
} from '../../components/BlockNoteBracketHighlightPlugin';

/**
 * Applies the bracket highlight plugin to a BlockNote editor instance
 * @param editor The BlockNote editor instance
 * @param onBracketClick Optional callback when a bracket is clicked
 */
export const applyBracketHighlightPlugin = (
  editor: BlockNoteEditor,
  onBracketClick?: OnBracketClick,
): void => {
  // Access the underlying TipTap/ProseMirror instance
  // BlockNote uses TipTap under the hood
  // This requires accessing internal APIs, so we disable linting for this section
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
  const tiptapEditor = (editor as any)._tiptapEditor;
  if (!tiptapEditor || !tiptapEditor.view) {
    console.warn('BlockNote editor TipTap instance not available');
    return;
  }

  try {
    const state = tiptapEditor.state;

    // Filter out any existing bracket highlight plugin
    const existingPlugins = state.plugins.filter((p: any) => {
      // Check both by key and by the key's string representation
      return p.key !== bracketHighlightPluginKey;
    });

    // Create new plugin with click handler
    const plugin = createBracketHighlightPlugin(onBracketClick);

    // Create new state with updated plugins
    const newState = state.reconfigure({
      plugins: [...existingPlugins, plugin],
    });

    tiptapEditor.view.updateState(newState);
  } catch (error) {
    console.error('Error applying bracket highlight plugin:', error);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
};
